import { onBeforeUnmount } from 'vue';

/**
 * Рефкаунтер захвата скролла — несколько модалок/тостов могут
 * одновременно требовать блокировку. Когда счётчик доходит до нуля,
 * возвращаем исходные стили body.
 */
const state = {
  count: 0,
  savedOverflow: '',
  savedPaddingRight: '',
  savedPosition: '',
  savedTop: '',
  savedWidth: '',
  scrollY: 0,
};

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iP(ad|hone|od)/.test(navigator.userAgent);
}

function lock(): void {
  if (typeof document === 'undefined') return;
  state.count += 1;
  if (state.count !== 1) return;

  const body = document.body;
  const html = document.documentElement;
  const scrollbarWidth = window.innerWidth - html.clientWidth;

  state.savedOverflow = body.style.overflow;
  state.savedPaddingRight = body.style.paddingRight;
  state.scrollY = window.scrollY;

  body.style.overflow = 'hidden';
  if (scrollbarWidth > 0) {
    body.style.paddingRight = `${scrollbarWidth}px`;
  }

  if (isIos()) {
    state.savedPosition = body.style.position;
    state.savedTop = body.style.top;
    state.savedWidth = body.style.width;
    body.style.position = 'fixed';
    body.style.top = `-${state.scrollY}px`;
    body.style.width = '100%';
  }
}

function unlock(): void {
  if (typeof document === 'undefined') return;
  if (state.count === 0) return;
  state.count -= 1;
  if (state.count !== 0) return;

  const body = document.body;
  body.style.overflow = state.savedOverflow;
  body.style.paddingRight = state.savedPaddingRight;

  if (isIos()) {
    body.style.position = state.savedPosition;
    body.style.top = state.savedTop;
    body.style.width = state.savedWidth;
    window.scrollTo(0, state.scrollY);
  }
}

export interface UseScrollLockReturn {
  readonly lock: () => void;
  readonly unlock: () => void;
  /** Внутреннее — для тестов. Не полагаться в прод-коде. */
  readonly _state: typeof state;
}

/**
 * Блокирует вертикальный scroll у `document.body`. Счётчик захватов —
 * модалки могут быть вложены; блокировка снимается, когда последний
 * потребитель отпустил.
 *
 * На iOS дополнительно использует `position: fixed` трюк, чтобы
 * предотвратить overscroll body при открытом bottom-sheet.
 *
 * Автоматически освобождает захват при unmount компонента, если был
 * вызван `lock()`.
 */
export function useScrollLock(): UseScrollLockReturn {
  let held = 0;

  function localLock(): void {
    held += 1;
    lock();
  }

  function localUnlock(): void {
    if (held === 0) return;
    held -= 1;
    unlock();
  }

  onBeforeUnmount(() => {
    while (held > 0) localUnlock();
  });

  return {
    lock: localLock,
    unlock: localUnlock,
    _state: state,
  };
}
